import { CategoryModel } from '@/http/models/ProductCategory'
import { toast } from 'sonner'

import { useState } from 'react'

import { ProductCategoryModel } from '@/types'

import { useFetchCategories } from './useFetchCategories'

type DialogMode = 'create' | 'edit' | 'view'

export const useCategoriesTable = () => {
  // Dialog state
  const [mode, setMode] = useState<DialogMode>('create')
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [editCategory, setEditCategory] = useState<ProductCategoryModel.ProductCategory | null>(
    null
  )

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] =
    useState<ProductCategoryModel.ProductCategory | null>(null)

  // Loading states
  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch categories data
  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchCategories({
      sort: null,
    })

  // Dialog handlers
  const openCreateDialog = () => {
    setMode('create')
    setEditCategory(null)
    setCategoryDialogOpen(true)
  }

  const openViewDialog = (category: ProductCategoryModel.ProductCategory) => {
    setMode('view')
    setEditCategory(category)
    setCategoryDialogOpen(true)
  }

  const openEditDialog = (category: ProductCategoryModel.ProductCategory) => {
    setMode('edit')
    setEditCategory(category)
    setCategoryDialogOpen(true)
  }

  const closeDialog = () => {
    setCategoryDialogOpen(false)
    setEditCategory(null)
  }

  // Delete handlers
  const handleDeleteClick = (category: ProductCategoryModel.ProductCategory) => {
    setCategoryToDelete(category)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete?._id) return

    setDeleteLoading(true)
    const response = await CategoryModel.delete({
      params: { id: categoryToDelete._id },
    })

    if (response.success) {
      toast.success('Category deleted successfully')
      setDeleteDialogOpen(false)
      setCategoryToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete category')
    }
    setDeleteLoading(false)
  }

  // Status toggle handler
  const toggleStatus = async (category: ProductCategoryModel.ProductCategory) => {
    const newStatus = category.status === 'active' ? 'inactive' : 'active'

    const response = await CategoryModel.update({
      params: { id: category._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Category status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  // CRUD handlers
  const handleCreateCategory = async (data: any) => {
    setCreateLoading(true)
    const response = await CategoryModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Category created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create category')
    }
    setCreateLoading(false)
  }

  const handleUpdateCategory = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await CategoryModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Category updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update category')
    }
    setUpdateLoading(false)
  }

  // Computed values
  const stats = {
    totalCategories: pagination.totalCount,
    activeCategories: data.filter((cat) => cat.status === 'active').length,
    inactiveCategories: data.filter((cat) => cat.status === 'inactive').length,
  }

  return {
    // Data
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,

    // Dialog state
    mode,
    categoryDialogOpen,
    editCategory,

    // Delete dialog state
    deleteDialogOpen,
    categoryToDelete,

    // Loading states
    createLoading,
    updateLoading,
    deleteLoading,

    // Pagination handlers
    setPage,
    setPageSize,
    refresh,

    // Dialog handlers
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,

    // Delete handlers
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,

    // CRUD handlers
    handleCreateCategory,
    handleUpdateCategory,
    toggleStatus,
  }
}
